// src/utils/fileUploadUtils.js

/**
 * File upload utility functions for handling attachments
 * Supports pdf (grievance/grant attachments); policy also allows doc/docx/txt
 */

import React from "react";

// Allowed file types for FileAttachment (matches current enforcement — PDF only)
export const ALLOWED_FILE_TYPES = {
  pdf: "application/pdf",
};

export const POLICY_ALLOWED_FILE_TYPES = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
};

// Maximum file size in bytes (5MB)
export const MAX_FILE_SIZE = 5 * 1024 * 1024;
export const POLICY_MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Get last extension from filename
 */
export const getExtension = (fileName = "") => {
  const parts = String(fileName).split(".");
  if (parts.length < 2) return "";
  return parts.pop().toLowerCase();
};

/**
 * Reject dangerous / double-extension style names
 */
export const hasDangerousFileName = (fileName = "") => {
  const name = String(fileName).trim();
  if (
    !name ||
    name.includes("..") ||
    name.includes("/") ||
    name.includes("\\") ||
    name.includes(";")
  ) {
    return true;
  }
  const lower = name.toLowerCase();
  const segments = lower.split(".");
  if (segments.length > 2) {
    const dangerous = [
      "html",
      "htm",
      "svg",
      "js",
      "exe",
      "php",
      "aspx",
      "jsp",
      "bat",
      "cmd",
      "sh",
    ];
    for (let i = 0; i < segments.length - 1; i++) {
      if (dangerous.includes(segments[i])) return true;
    }
  }
  return false;
};

const readFileHeader = (file, length = 64) =>
  new Promise((resolve, reject) => {
    const blob = file.slice(0, length);
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(new Error("Failed to read file content"));
    reader.readAsArrayBuffer(blob);
  });

const bytesStartWith = (bytes, signature) => {
  if (!bytes || bytes.length < signature.length) return false;
  return signature.every((b, i) => bytes[i] === b);
};

const bytesToAscii = (bytes) =>
  Array.from(bytes || [])
    .map((b) => (b >= 32 && b < 127 ? String.fromCharCode(b) : " "))
    .join("");

/**
 * Magic-byte / content signature check for allowlisted types
 */
export const matchesFileSignature = async (file, extension) => {
  const bytes = await readFileHeader(file, 512);
  const ascii = bytesToAscii(bytes).toLowerCase();

  // Reject HTML/script/SVG payloads disguised as documents/images
  const looksLikeMarkup =
    ascii.includes("<html") ||
    ascii.includes("<!doctype") ||
    ascii.includes("<script") ||
    ascii.includes("<?php") ||
    ascii.includes("<svg");

  if (looksLikeMarkup) {
    return false;
  }

  switch (extension) {
    case "pdf":
      return bytesStartWith(bytes, [0x25, 0x50, 0x44, 0x46]); // %PDF
    case "jpg":
    case "jpeg":
      return bytesStartWith(bytes, [0xff, 0xd8, 0xff]);
    case "png":
      return bytesStartWith(bytes, [0x89, 0x50, 0x4e, 0x47]);
    case "doc":
    case "xls":
      return bytesStartWith(bytes, [0xd0, 0xcf, 0x11, 0xe0]); // OLE
    case "docx":
    case "xlsx":
      return bytesStartWith(bytes, [0x50, 0x4b]); // ZIP/OOXML
    case "txt":
      return true;
    default:
      return false;
  }
};

/**
 * Full validation for a single file against an allowlist map
 */
export const validateFileSecure = async (
  file,
  allowedTypes = ALLOWED_FILE_TYPES,
  maxSize = MAX_FILE_SIZE
) => {
  if (!file || !(file instanceof Blob)) {
    return { isValid: false, message: "Invalid file." };
  }

  const fileName = file.name || "";
  const extension = getExtension(fileName);

  if (hasDangerousFileName(fileName)) {
    return {
      isValid: false,
      message: `Unsafe file name rejected: ${fileName}`,
    };
  }

  if (!Object.keys(allowedTypes).includes(extension)) {
    return {
      isValid: false,
      message: `File type not allowed: ${fileName}. Allowed: ${Object.keys(
        allowedTypes
      )
        .join(", ")
        .toUpperCase()}`,
    };
  }

  if (file.size > maxSize) {
    return {
      isValid: false,
      message: `File too large: ${fileName} (max: ${formatFileSize(maxSize)})`,
    };
  }

  const expectedMime = allowedTypes[extension];
  if (file.type && file.type !== expectedMime) {
    const mimeOk =
      file.type === expectedMime ||
      (extension === "jpg" && file.type === "image/jpeg") ||
      (extension === "doc" &&
        (file.type === "application/msword" ||
          file.type === "application/octet-stream")) ||
      (extension === "docx" &&
        (file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          file.type === "application/zip" ||
          file.type === "application/octet-stream")) ||
      (extension === "txt" &&
        (file.type === "text/plain" || file.type.startsWith("text/")));

    if (!mimeOk && file.type !== "application/octet-stream") {
      return {
        isValid: false,
        message: `File content type does not match extension: ${fileName}`,
      };
    }
  }

  const signatureOk = await matchesFileSignature(file, extension);
  if (!signatureOk) {
    return {
      isValid: false,
      message: `Invalid file content. "${fileName}" is not a valid ${extension.toUpperCase()} file.`,
    };
  }

  return {
    isValid: true,
    message: "File is valid",
    extension,
    mime: expectedMime,
  };
};

/**
 * Check if file type is allowed (extension whitelist)
 */
export const isFileTypeAllowed = (file) => {
  if (hasDangerousFileName(file?.name)) return false;
  const extension = getExtension(file?.name);
  return Object.keys(ALLOWED_FILE_TYPES).includes(extension);
};

/**
 * Check if file size is within limits
 */
export const isFileSizeAllowed = (file, maxSize = MAX_FILE_SIZE) => {
  return file.size <= maxSize;
};

/**
 * Format file size to human-readable format
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Get file icon based on file type
 */
export const getFileIconClass = (fileName) => {
  const extension = getExtension(fileName);

  switch (extension) {
    case "pdf":
      return "bi bi-file-earmark-pdf";
    case "doc":
    case "docx":
      return "bi bi-file-earmark-word";
    case "xls":
    case "xlsx":
      return "bi bi-file-earmark-excel";
    case "txt":
      return "bi bi-file-earmark-text";
    case "png":
    case "jpg":
    case "jpeg":
      return "bi bi-file-earmark-image";
    default:
      return "bi bi-file-earmark";
  }
};

/**
 * Check if file is a safe image type (not SVG)
 */
export const isImageFile = (file) => {
  const ext = getExtension(file?.name);
  return (
    (ext === "jpg" || ext === "jpeg" || ext === "png") &&
    (file.type === "image/jpeg" ||
      file.type === "image/png" ||
      !file.type)
  );
};

/**
 * Create file preview object
 */
export const createFilePreview = (file) => {
  const safeImage = isImageFile(file);
  return {
    file,
    name: file.name,
    size: file.size,
    type: ALLOWED_FILE_TYPES[getExtension(file.name)] || file.type,
    icon: getFileIconClass(file.name),
    preview: safeImage ? URL.createObjectURL(file) : null,
    isImage: safeImage,
  };
};

/**
 * Sync validate (extension + size only) — kept for compatibility
 */
export const validateFiles = (fileList, options = {}) => {
  const { maxFiles = 5, maxSize = MAX_FILE_SIZE } = options;
  const files = Array.from(fileList || []);

  const result = {
    validFiles: [],
    errors: [],
    hasErrors: false,
  };

  if (files.length > maxFiles) {
    result.errors.push(`You can upload a maximum of ${maxFiles} files.`);
    result.hasErrors = true;
    return result;
  }

  files.forEach((file) => {
    if (!isFileTypeAllowed(file)) {
      result.errors.push(`File type not allowed: ${file.name}`);
      result.hasErrors = true;
      return;
    }

    if (!isFileSizeAllowed(file, maxSize)) {
      result.errors.push(
        `File too large: ${file.name} (max: ${formatFileSize(maxSize)})`
      );
      result.hasErrors = true;
      return;
    }

    result.validFiles.push(file);
  });

  return result;
};

/**
 * Async validate with content signature checks (use this for uploads)
 */
export const validateFilesAsync = async (fileList, options = {}) => {
  const {
    maxFiles = 5,
    maxSize = MAX_FILE_SIZE,
    allowedTypes = ALLOWED_FILE_TYPES,
  } = options;
  const files = Array.from(fileList || []);

  const result = {
    validFiles: [],
    errors: [],
    hasErrors: false,
  };

  if (files.length > maxFiles) {
    result.errors.push(`You can upload a maximum of ${maxFiles} files.`);
    result.hasErrors = true;
    return result;
  }

  for (const file of files) {
    const check = await validateFileSecure(file, allowedTypes, maxSize);
    if (!check.isValid) {
      result.errors.push(check.message);
      result.hasErrors = true;
      continue;
    }
    result.validFiles.push(file);
  }

  return result;
};

/**
 * Upload files to server (stub — unused by live flows)
 */
export const uploadFiles = async (files, endpoint, options = {}) => {
  const formData = new FormData();

  files.forEach((file, index) => {
    formData.append(`file${index}`, file);
  });

  if (options.additionalData) {
    Object.entries(options.additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  try {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          fileNames: files.map((file) => file.name),
          message: "Files uploaded successfully",
        });
      }, 1000);
    });
  } catch (error) {
    console.error("Error uploading files:", error);
    throw error;
  }
};

/**
 * Revoke file preview URLs to prevent memory leaks
 */
export const revokeFilePreviews = (previews) => {
  previews.forEach((preview) => {
    if (preview.preview) {
      URL.revokeObjectURL(preview.preview);
    }
  });
};

/**
 * Custom hook for managing file uploads
 */
export const useFileUpload = (options = {}) => {
  const [files, setFiles] = React.useState([]);
  const [previews, setPreviews] = React.useState([]);
  const [errors, setErrors] = React.useState([]);

  const handleFileChange = async (e) => {
    const validation = await validateFilesAsync(e.target.files, options);

    if (validation.hasErrors) {
      setErrors(validation.errors);
      return;
    }

    setErrors([]);

    const newFiles = [...files, ...validation.validFiles];
    setFiles(newFiles);

    const newPreviews = validation.validFiles.map(createFilePreview);
    setPreviews([...previews, ...newPreviews]);
  };

  const removeFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);

    const updatedPreviews = [...previews];
    if (updatedPreviews[index].preview) {
      URL.revokeObjectURL(updatedPreviews[index].preview);
    }
    updatedPreviews.splice(index, 1);

    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
  };

  const clearFiles = () => {
    revokeFilePreviews(previews);
    setFiles([]);
    setPreviews([]);
    setErrors([]);
  };

  React.useEffect(() => {
    return () => revokeFilePreviews(previews);
  }, []);

  return {
    files,
    previews,
    errors,
    handleFileChange,
    removeFile,
    clearFiles,
  };
};

/**
 * Convert file to base64 for policy uploads
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Validate file type for policies (async — extension + MIME + content)
 */
export const validatePolicyFile = async (file) => {
  const check = await validateFileSecure(
    file,
    POLICY_ALLOWED_FILE_TYPES,
    POLICY_MAX_FILE_SIZE
  );
  if (!check.isValid) {
    throw new Error(
      check.message || "Only PDF, DOC, DOCX, and TXT files are allowed"
    );
  }
  return true;
};

export default {
  validateFiles,
  validateFilesAsync,
  validateFileSecure,
  createFilePreview,
  formatFileSize,
  getFileIconClass,
  isFileTypeAllowed,
  isFileSizeAllowed,
  isImageFile,
  uploadFiles,
  revokeFilePreviews,
  useFileUpload,
  ALLOWED_FILE_TYPES,
  POLICY_ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  POLICY_MAX_FILE_SIZE,
  fileToBase64,
  validatePolicyFile,
  getExtension,
  hasDangerousFileName,
  matchesFileSignature,
};
